export const NAMING_MATCH_MODES = [
  { id: 'contains', label: 'contain' },
  { id: 'startsWith', label: 'start with' },
  { id: 'endsWith', label: 'end with' },
  { id: 'equals', label: 'equal' }
];

export const NAMING_RULE_KINDS = [
  { id: 'match', label: 'must' },
  { id: 'mismatch', label: 'must not' }
];

export const NAMING_FIELD_TOKENS = [
  { id: 'serialnumber', label: 'Serial' },
  { id: 'manufacturer', label: 'Manufacturer' },
  { id: 'model', label: 'Model' },
  { id: 'assigned_to', label: 'Assigned to' },
  { id: 'chassistype', label: 'Chassis type' }
];

const TOKEN_ALIASES = {
  serialnumber: ['serial', 'serialnumber'],
  serial: ['serial', 'serialnumber'],
  manufacturer: ['manufacturer'],
  model: ['model'],
  assigned_to: ['assigned_to', 'assignedto'],
  assignedto: ['assigned_to', 'assignedto'],
  chassistype: ['chassistype', 'chassis_type', 'type'],
  type: ['chassistype', 'chassis_type', 'type']
};

function isKnownToken(fieldName) {
  const key = String(fieldName || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(TOKEN_ALIASES, key)
    || NAMING_FIELD_TOKENS.some((token) => token.id === key);
}

function getAssetField(asset, fieldName) {
  if (!asset || typeof asset !== 'object') return '';
  const key = String(fieldName || '').toLowerCase().replace(/[{}]/g, '');
  const aliases = TOKEN_ALIASES[key] || [key];

  for (const alias of aliases) {
    if (alias === 'chassistype' || alias === 'chassis_type' || alias === 'type') {
      const chassis = asset.ChassisType ?? asset.chassistype ?? asset.chassisType ?? asset.type;
      if (chassis != null && String(chassis).trim() !== '') return String(chassis).trim();
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(asset, alias) && asset[alias] != null) {
      const value = String(asset[alias]).trim();
      if (value) return value;
    }
    const match = Object.keys(asset).find((assetKey) => assetKey.toLowerCase() === alias);
    if (match && asset[match] != null) {
      const value = String(asset[match]).trim();
      if (value) return value;
    }
  }
  return '';
}

export function normalizeNamingMatchMode(mode) {
  return NAMING_MATCH_MODES.some((item) => item.id === mode) ? mode : 'contains';
}

export function normalizeNamingRuleKind(kind) {
  return kind === 'match' ? 'match' : 'mismatch';
}

export function getNamingMatchModeLabel(mode) {
  const match = NAMING_MATCH_MODES.find((item) => item.id === mode);
  return match ? match.label : 'contain';
}

export function expandNamingPattern(pattern, asset) {
  return String(pattern || '').replace(/\{([a-z0-9_-]+)\}/gi, (token, name) => {
    if (!isKnownToken(name)) return token;
    const value = getAssetField(asset, name);
    return value === '' ? token : value;
  });
}

function patternUsesUnresolvedToken(pattern, asset) {
  const tokens = String(pattern || '').match(/\{([a-z0-9_-]+)\}/gi) || [];
  return tokens.some((token) => {
    const name = token.slice(1, -1);
    return isKnownToken(name) && getAssetField(asset, name) === '';
  });
}

export function assetMatchesNamingConvention(asset, pattern, mode = 'contains') {
  const trimmedPattern = String(pattern || '').trim();
  if (!trimmedPattern) return true;

  const name = String(asset?.name ?? '').trim();
  if (!name) return false;
  if (patternUsesUnresolvedToken(trimmedPattern, asset)) return false;

  const expected = expandNamingPattern(trimmedPattern, asset).trim();
  if (!expected) return false;

  const nameValue = name.toLowerCase();
  const expectedValue = expected.toLowerCase();
  switch (normalizeNamingMatchMode(mode)) {
    case 'startsWith':
      return nameValue.startsWith(expectedValue);
    case 'endsWith':
      return nameValue.endsWith(expectedValue);
    case 'equals':
      return nameValue === expectedValue;
    default:
      return nameValue.includes(expectedValue);
  }
}

export function isNamingFilterActive(mode, pattern) {
  return mode === 'naming' && String(pattern || '').trim() !== '';
}

export function assetPassesNamingFilter(asset, pattern, mode = 'contains', ruleKind = 'mismatch') {
  const matches = assetMatchesNamingConvention(asset, pattern, mode);
  return normalizeNamingRuleKind(ruleKind) === 'match' ? matches : !matches;
}

export function describeNamingFilter(pattern, mode, ruleKind = 'mismatch') {
  const polarity = NAMING_RULE_KINDS.find((item) => item.id === normalizeNamingRuleKind(ruleKind));
  return `Showing names that ${polarity ? polarity.label : 'must not'} ${getNamingMatchModeLabel(mode)} ${pattern}`;
}
